/* =========================================================
   LES PETITS MAROO — COOKIE CONSENT
   MASTER — 09/2026
   Shopify Customer Privacy API
   ========================================================= */

(() => {
  'use strict';

  /* ---------------------------------------------------------
     GARDE ANTI DOUBLE-INITIALISATION
     --------------------------------------------------------- */

  if (window.LPMConsent?.initialized) return;

  const root = document.querySelector('[data-lpm-cookie-consent]');
  if (!root) return;

  /* ---------------------------------------------------------
     CONFIG
     --------------------------------------------------------- */

  const config = {
    delay: Math.max(0, Number(root.dataset.delay) || 900),
    consentVersion: root.dataset.consentVersion || '2026-09-v1',
    designMode: root.dataset.designMode === 'true',
    editorPreview: root.dataset.editorPreview === 'true',
    exitDuration: 180
  };

  const CONSENT_KEYS = ['preferences', 'analytics', 'marketing'];

  /* ---------------------------------------------------------
     DOM
     --------------------------------------------------------- */

  const card = root.querySelector('.lpm-consent__card');
  const views = [...root.querySelectorAll('[data-lpm-view]')];
  const status = root.querySelector('[data-lpm-status]');

  const inputs = {
    preferences: root.querySelector(
      '[data-lpm-consent-input="preferences"]'
    ),
    analytics: root.querySelector(
      '[data-lpm-consent-input="analytics"]'
    ),
    marketing: root.querySelector(
      '[data-lpm-consent-input="marketing"]'
    )
  };

  const actionButtons = [
    ...root.querySelectorAll('[data-lpm-action]')
  ];

  /* ---------------------------------------------------------
     ÉTAT
     --------------------------------------------------------- */

  const state = {
    api: null,
    apiPromise: null,
    visible: false,
    saving: false,
    manualOpen: false,
    currentView: 'prompt',
    history: []
  };

  /* ---------------------------------------------------------
     OUTILS
     --------------------------------------------------------- */

  function clearStatus() {
    if (!status) return;

    status.textContent = '';
    status.classList.remove('is-error');
  }

  function showError(message) {
    if (!status) return;

    status.textContent = message;
    status.classList.add('is-error');
  }

  function setSaving(isSaving) {
    state.saving = isSaving;

    actionButtons.forEach((button) => {
      const action = button.dataset.lpmAction;

      if (
        action === 'accept' ||
        action === 'reject' ||
        action === 'save'
      ) {
        button.disabled = isSaving;
      }
    });
  }

  function normaliseConsentValue(value) {
    return value === 'yes';
  }

  function hasExplicitValue(value) {
    return value === 'yes' || value === 'no';
  }

  function getVisitorConsent() {
    if (!state.api?.currentVisitorConsent) {
      return {
        preferences: '',
        analytics: '',
        marketing: '',
        sale_of_data: ''
      };
    }

    return state.api.currentVisitorConsent();
  }

  function hasCompleteConsentDecision() {
    const consent = getVisitorConsent();

    return CONSENT_KEYS.every((key) =>
      hasExplicitValue(consent[key])
    );
  }

  function syncInputsFromShopify() {
    const consent = getVisitorConsent();

    CONSENT_KEYS.forEach((key) => {
      if (!inputs[key]) return;

      /*
       * Important :
       * une absence de choix Shopify n'est jamais transformée
       * en case cochée par défaut.
       */
      inputs[key].checked =
        normaliseConsentValue(consent[key]);
    });
  }

  /* ---------------------------------------------------------
     GESTION DES ÉCRANS
     --------------------------------------------------------- */

  function showView(name, rememberPrevious = true) {
    const target = views.find(
      (view) => view.dataset.lpmView === name
    );

    if (!target) return;

    if (
      rememberPrevious &&
      state.currentView !== name
    ) {
      state.history.push(state.currentView);
    }

    views.forEach((view) => {
      view.hidden = view !== target;
    });

    state.currentView = name;

    if (name === 'preferences') {
      syncInputsFromShopify();
    }

    clearStatus();
  }

  function goBack() {
    const previous = state.history.pop() || 'prompt';

    showView(previous, false);
  }

  /* ---------------------------------------------------------
     AFFICHAGE / FERMETURE
     --------------------------------------------------------- */

  function showBanner({
    view = 'prompt',
    manual = false
  } = {}) {
    state.manualOpen = manual;
    state.history = [];
    state.currentView = view;

    root.classList.toggle('is-manual', manual);

    views.forEach((item) => {
      item.hidden =
        item.dataset.lpmView !== view;
    });

    if (view === 'preferences') {
      syncInputsFromShopify();
    }

    clearStatus();

    root.hidden = false;

    /*
     * Double RAF :
     * permet au navigateur d'enregistrer l'état initial
     * avant d'appliquer la transition CSS.
     */
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        root.classList.remove('is-leaving');
        root.classList.add('is-visible');

        state.visible = true;
      });
    });
  }

  function hideBanner() {
    if (!state.visible && root.hidden) return;

    root.classList.remove('is-visible');
    root.classList.add('is-leaving');

    state.visible = false;

    window.setTimeout(() => {
      root.hidden = true;

      root.classList.remove(
        'is-leaving',
        'is-manual'
      );

      state.manualOpen = false;
      state.history = [];
      state.currentView = 'prompt';

      views.forEach((view) => {
        view.hidden =
          view.dataset.lpmView !== 'prompt';
      });

      clearStatus();
    }, config.exitDuration);
  }

  /* ---------------------------------------------------------
     CHARGEMENT CUSTOMER PRIVACY API
     --------------------------------------------------------- */

  function loadCustomerPrivacyApi() {
    if (state.api) {
      return Promise.resolve(state.api);
    }

    if (state.apiPromise) {
      return state.apiPromise;
    }

    state.apiPromise = new Promise((resolve, reject) => {
      /*
       * Elle peut déjà être disponible si un autre composant
       * Shopify l'a chargée.
       */
      if (window.Shopify?.customerPrivacy) {
        state.api = window.Shopify.customerPrivacy;
        resolve(state.api);
        return;
      }

      if (
        !window.Shopify ||
        typeof window.Shopify.loadFeatures !== 'function'
      ) {
        reject(
          new Error(
            'Shopify.loadFeatures est indisponible.'
          )
        );
        return;
      }

      window.Shopify.loadFeatures(
        [
          {
            name: 'consent-tracking-api',
            version: '0.1'
          }
        ],
        (error) => {
          if (error) {
            reject(error);
            return;
          }

          if (!window.Shopify?.customerPrivacy) {
            reject(
              new Error(
                'La Customer Privacy API Shopify ne s’est pas initialisée.'
              )
            );
            return;
          }

          state.api = window.Shopify.customerPrivacy;

          resolve(state.api);
        }
      );
    }).catch((error) => {
      state.apiPromise = null;
      throw error;
    });

    return state.apiPromise;
  }

  /* ---------------------------------------------------------
     ENREGISTREMENT SHOPIFY
     --------------------------------------------------------- */

  function saveConsent(consent) {
    clearStatus();

    /*
     * Mode éditeur :
     * surtout ne jamais écrire un vrai consentement pendant
     * qu'on travaille sur le thème.
     */
    if (config.designMode) {
      hideBanner();
      return Promise.resolve();
    }

    if (!state.api?.setTrackingConsent) {
      return Promise.reject(
        new Error(
          'Le service de consentement Shopify est indisponible.'
        )
      );
    }

    setSaving(true);

    return new Promise((resolve, reject) => {
      const payload = {
        preferences: Boolean(consent.preferences),
        analytics: Boolean(consent.analytics),
        marketing: Boolean(consent.marketing),

        /*
         * Métadonnées stockées PAR SHOPIFY.
         * Pas de cookie LPM / pas de localStorage parallèle.
         */
        metafields: [
          {
            key: 'lpm-consent-version',
            value: config.consentVersion
          },
          {
            key: 'lpm-consent-date',
            value: new Date().toISOString()
          }
        ]
      };

      state.api.setTrackingConsent(
        payload,
        (result) => {
          setSaving(false);

          if (result?.error) {
            reject(
              new Error(
                Array.isArray(result.error)
                  ? result.error.join(' ')
                  : String(result.error)
              )
            );
            return;
          }

          resolve();
        }
      );
    });
  }

  async function handleAccept() {
    try {
      await saveConsent({
        preferences: true,
        analytics: true,
        marketing: true
      });

      hideBanner();
    } catch (error) {
      console.error('[LPM Consent]', error);

      showError(
        "Oups, je n’ai pas réussi à enregistrer ton choix. Réessaie dans un instant. 💜"
      );
    }
  }

  async function handleReject() {
    try {
      await saveConsent({
        preferences: false,
        analytics: false,
        marketing: false
      });

      hideBanner();
    } catch (error) {
      console.error('[LPM Consent]', error);

      showError(
        "Oups, je n’ai pas réussi à enregistrer ton choix. Réessaie dans un instant. 💜"
      );
    }
  }

  async function handleSavePreferences() {
    try {
      await saveConsent({
        preferences: Boolean(
          inputs.preferences?.checked
        ),
        analytics: Boolean(
          inputs.analytics?.checked
        ),
        marketing: Boolean(
          inputs.marketing?.checked
        )
      });

      hideBanner();
    } catch (error) {
      console.error('[LPM Consent]', error);

      showError(
        "Oups, je n’ai pas réussi à enregistrer tes préférences. Réessaie dans un instant. 💜"
      );
    }
  }

  /* ---------------------------------------------------------
     OUVERTURE MANUELLE DEPUIS LE FOOTER
     --------------------------------------------------------- */

  async function openPreferences() {
    try {
      await loadCustomerPrivacyApi();

      state.manualOpen = true;

      showBanner({
        view: 'preferences',
        manual: true
      });
    } catch (error) {
      console.error('[LPM Consent]', error);
    }
  }

  /* ---------------------------------------------------------
     CLICS DANS LA CARTE
     --------------------------------------------------------- */

  root.addEventListener('click', (event) => {
    const button = event.target.closest(
      '[data-lpm-action]'
    );

    if (!button || state.saving) return;

    const action = button.dataset.lpmAction;

    switch (action) {
      case 'accept':
        handleAccept();
        break;

      case 'reject':
        handleReject();
        break;

      case 'details':
        showView('details');
        break;

      case 'preferences':
        showView('preferences');
        break;

      case 'back':
        goBack();
        break;

      case 'save':
        handleSavePreferences();
        break;

      case 'close':
        /*
         * Fermer sans enregistrer n'est autorisé que lorsque
         * l'utilisateur a lui-même rouvert les préférences.
         */
        if (state.manualOpen) {
          hideBanner();
        }
        break;
    }
  });

  /* ---------------------------------------------------------
     LIEN GLOBAL "GÉRER MES COOKIES"
     --------------------------------------------------------- */

  document.addEventListener('click', (event) => {
    const trigger = event.target.closest(
  '[data-lpm-cookie-settings], a[href="#lpm-cookie-settings"]'
);

    if (!trigger) return;

    event.preventDefault();

    openPreferences();
  });

  /* ---------------------------------------------------------
     CHANGEMENT DE CONSENTEMENT DEPUIS SHOPIFY / PIXEL / AUTRE
     --------------------------------------------------------- */

  document.addEventListener(
    'visitorConsentCollected',
    () => {
      if (!state.api) return;

      syncInputsFromShopify();

      /*
       * Si un autre composant a enregistré un choix complet
       * pendant que notre prompt automatique était affiché,
       * on évite deux interfaces concurrentes.
       */
      if (
        state.visible &&
        !state.manualOpen &&
        hasCompleteConsentDecision()
      ) {
        hideBanner();
      }
    }
  );

  /* ---------------------------------------------------------
     INITIALISATION
     --------------------------------------------------------- */

  async function init() {
    /*
     * Shopify Theme Editor :
     * aperçu visuel uniquement.
     */
    if (config.designMode) {
      if (config.editorPreview) {
        window.setTimeout(() => {
          showBanner({
            view: 'prompt',
            manual: false
          });
        }, 150);
      }

      return;
    }

    const startedAt = performance.now();

    try {
      const api = await loadCustomerPrivacyApi();

      /*
       * shouldShowBanner() tient compte des réglages
       * géographiques Shopify et du consentement visiteur.
       */
      const shouldShow =
        typeof api.shouldShowBanner === 'function'
          ? api.shouldShowBanner()
          : false;

      /*
       * Sécurité supplémentaire :
       * si les 3 choix sont déjà explicitement enregistrés,
       * on ne réaffiche jamais notre prompt.
       */
      if (
        !shouldShow ||
        hasCompleteConsentDecision()
      ) {
        return;
      }

      const elapsed = performance.now() - startedAt;

      const remainingDelay = Math.max(
        0,
        config.delay - elapsed
      );

      window.setTimeout(() => {
        /*
         * On relit l'état juste avant d'afficher.
         * Le consentement a pu changer entre-temps.
         */
        if (
          hasCompleteConsentDecision()
        ) {
          return;
        }

        showBanner({
          view: 'prompt',
          manual: false
        });
      }, remainingDelay);
    } catch (error) {
      /*
       * Fail closed :
       * si l'API privacy Shopify est indisponible,
       * notre interface ne prétend PAS pouvoir enregistrer
       * un consentement.
       */
      console.error(
        '[LPM Consent] Impossible de charger la Customer Privacy API.',
        error
      );
    }
  }

  /* ---------------------------------------------------------
     API PUBLIQUE LPM
     --------------------------------------------------------- */

  window.LPMConsent = {
    initialized: true,

    openPreferences,

    close: hideBanner,

    getConsent() {
      return state.api
        ? getVisitorConsent()
        : null;
    }
  };

  init();
})();